/* MUVAZI: manual product-image galleries and the existing LAST DROPS rail.
 * Load once with defer. No framework, product-JSON request or autoplay.
 */
(() => {
  'use strict';
  if (customElements.get('muvazi-product-gallery')) return;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const nearViewport = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.loadImage(1);
      nearViewport.unobserve(entry.target);
    });
  }, { rootMargin: '600px 0px' }) : null;

  class MuvaziProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      // Custom elements may connect before their children have been parsed.
      const childrenReady = () => this.querySelector('.muvazi-gallery__track') &&
        (Number(this.dataset.count) < 2 || this.querySelector('[data-muvazi-gallery-images]')?.textContent.trim().endsWith(']'));
      if (!childrenReady()) {
        this.pending = new MutationObserver(() => {
          if (childrenReady()) {
            this.pending.disconnect();
            this.initialize();
          }
        });
        this.pending.observe(this, { childList: true, subtree: true, characterData: true });
        return;
      }
      this.initialize();
    }

    initialize() {
      if (this.abort || Number(this.dataset.count) < 2) return;
      this.viewport = this.querySelector('.muvazi-gallery__viewport');
      this.track = this.querySelector('.muvazi-gallery__track');
      this.firstLink = this.track?.querySelector('a');
      try { this.images = JSON.parse(this.querySelector('[data-muvazi-gallery-images]')?.textContent || '[]'); }
      catch { return; }
      if (!this.firstLink || this.images.length < 2) return;
      // Reconnection/theme-editor re-render must not duplicate slides.
      this.track.querySelectorAll('[data-muvazi-generated]').forEach((node) => node.remove());
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.index = 0;
      this.width = this.viewport.clientWidth;
      this.blockClick = false;
      this.firstLink.tabIndex = 0;
      this.firstLink.removeAttribute('aria-hidden');
      this.slides = [this.firstLink];
      const firstImage = this.firstLink.querySelector('img');
      this.images.slice(1).forEach((data, offset) => {
        const link = this.firstLink.cloneNode(false);
        link.dataset.muvaziGenerated = '';
        link.tabIndex = -1;
        link.setAttribute('aria-hidden', 'true');
        link.setAttribute('aria-label', `${data.alt || firstImage.alt} — ${this.description(offset + 1)}`);
        const img = document.createElement('img');
        img.className = 'muvazi-gallery__image';
        img.alt = data.alt || firstImage.alt;
        img.width = data.width;
        img.height = data.height;
        img.sizes = firstImage.sizes;
        img.decoding = 'async';
        img.draggable = false;
        link.append(img);
        this.track.append(link);
        this.slides.push(link);
      });
      this.previous = this.querySelector('.muvazi-gallery__arrow--previous');
      this.next = this.querySelector('.muvazi-gallery__arrow--next');
      this.status = this.querySelector('.muvazi-gallery__status');
      this.previous.disabled = true;
      this.next.disabled = false;
      this.status.textContent = '';
      this.style.setProperty('--muvazi-gallery-index', 0);
      this.previous.addEventListener('click', () => this.go(this.index - 1), { signal });
      this.next.addEventListener('click', () => this.go(this.index + 1), { signal });
      this.viewport.addEventListener('pointerdown', (event) => this.start(event), { signal, passive: true });
      this.viewport.addEventListener('pointermove', (event) => this.move(event), { signal });
      this.viewport.addEventListener('pointerup', (event) => this.end(event), { signal });
      this.viewport.addEventListener('pointercancel', () => this.cancel(), { signal });
      this.viewport.addEventListener('lostpointercapture', (event) => {
        // Touch starts with implicit capture on the image. Moving capture to
        // the viewport must not cancel the gesture when that image releases it.
        if (event.target === this.viewport && event.pointerId === this.gesture?.id) this.cancel();
      }, { signal });
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.viewport.addEventListener('click', (event) => {
        if (this.blockClick && event.detail !== 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal });
      this.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const destinations = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.images.length - 1 };
        if (!(event.key in destinations)) return;
        event.preventDefault();
        event.stopPropagation();
        const imageFocused = this.track.contains(document.activeElement);
        this.go(destinations[event.key]);
        if (imageFocused) this.slides[this.index].focus({ preventScroll: true });
      }, { signal });
      this.resize = () => {
        const width = this.viewport.clientWidth;
        if (!width || width === this.width) return;
        this.width = width;
        this.gesture = null;
        this.classList.remove('is-dragging');
        this.classList.add('is-resizing');
        this.paint();
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.classList.remove('is-resizing'));
      };
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.resize);
        this.observer.observe(this.viewport);
      } else window.addEventListener('resize', this.resize, { signal });
      this.setAttribute('data-ready', '');
      this.style.setProperty('--muvazi-gallery-count', this.images.length);
      this.paint();
      if (nearViewport) nearViewport.observe(this);
    }

    description(index) {
      return `${this.dataset.imageLabel || 'Bild'} ${index + 1} ${this.dataset.ofLabel || 'von'} ${this.images.length}`;
    }

    loadImage(index) {
      const image = this.slides?.[index]?.querySelector('img');
      if (!image || image.hasAttribute('src')) return;
      const data = this.images[index];
      image.srcset = data.srcset;
      image.src = data.src;
    }

    start(event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.blockClick = false;
      this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, dragging: false };
      this.loadImage(this.index - 1);
      this.loadImage(this.index + 1);
    }

    move(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (!gesture.dragging) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { this.cancel(); return; }
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        gesture.dragging = true;
        this.blockClick = true;
        this.classList.add('is-dragging');
        this.viewport.setPointerCapture(event.pointerId);
      }
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();
      gesture.dx = dx;
      const outside = (this.index === 0 && dx > 0) || (this.index === this.images.length - 1 && dx < 0);
      this.paint(outside ? dx * 0.18 : Math.max(-this.width, Math.min(this.width, dx)));
    }

    end(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      this.gesture = null;
      this.classList.remove('is-dragging');
      if (gesture.dragging) {
        const threshold = Math.min(60, Math.max(24, this.width * 0.14));
        this.go(this.index + (Math.abs(gesture.dx) >= threshold ? (gesture.dx < 0 ? 1 : -1) : 0));
      }
      if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
    }

    cancel() {
      if (!this.gesture) return;
      this.gesture = null;
      this.classList.remove('is-dragging');
      this.paint();
    }

    go(index) {
      const nextIndex = Math.max(0, Math.min(this.images.length - 1, index));
      this.loadImage(nextIndex);

      const targetImage = this.slides[nextIndex]?.querySelector('img');
      const request = (this.navigationRequest || 0) + 1;
      this.navigationRequest = request;

      const waitForImage =
        nextIndex !== this.index &&
        window.matchMedia('(max-width: 749px)').matches &&
        this.closest('#ProductGridContainer') &&
        targetImage &&
        !targetImage.complete;

      if (waitForImage) {
        const reveal = () => {
          if (request !== this.navigationRequest || !this.isConnected) return;

          const decoding =
            typeof targetImage.decode === 'function'
              ? targetImage.decode()
              : Promise.resolve();

          decoding
            .catch(() => {})
            .then(() => {
              if (
                request === this.navigationRequest &&
                this.isConnected
              ) {
                this.go(nextIndex);
              }
            });
        };

        targetImage.addEventListener('load', reveal, {
          once: true,
          signal: this.abort.signal
        });

        targetImage.addEventListener(
          'error',
          () => {
            if (request === this.navigationRequest) {
              this.navigationRequest += 1;
            }
          },
          {
            once: true,
            signal: this.abort.signal
          }
        );

        return;
      }

      if (
        nextIndex !== this.index &&
        targetImage?.complete &&
        !targetImage.naturalWidth
      ) {
        return;
      }

      const changed = nextIndex !== this.index;
      this.index = nextIndex;
      this.loadImage(this.index + 1);

      this.slides.forEach((slide, position) => {
        slide.tabIndex = position === this.index ? 0 : -1;

        if (position === this.index) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.images.length - 1;
      this.style.setProperty('--muvazi-gallery-index', this.index);
      this.paint();

      if (changed) {
        this.status.textContent = this.description(this.index);
      }
    }

    paint(offset = 0) {
      const instantMobile =
        window.matchMedia('(max-width: 749px)').matches &&
        this.closest('#ProductGridContainer');

      if (instantMobile) offset = 0;

      if (this.track) {
        this.track.style.transform =
          `translate3d(${-this.index * this.width + offset}px, 0, 0)`;
      }
    }

    disconnectedCallback() {
      this.pending?.disconnect();
      this.abort?.abort();
      this.abort = null;
      this.observer?.disconnect();
      nearViewport?.unobserve(this);
      cancelAnimationFrame(this.resizeFrame);
      this.removeAttribute('data-ready');
      this.classList.remove('is-dragging', 'is-resizing');
    }
  }

  class MuvaziProductRail extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.slider = this.querySelector('.luxury-slider__products');
      this.previous = this.querySelector('.luxury-slider__arrow--prev');
      this.next = this.querySelector('.luxury-slider__arrow--next');
      if (!this.slider || !this.previous || !this.next) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.setAttribute('data-rail-ready', '');
      this.classList.add('products-visible');
      if (!reducedMotion() && 'IntersectionObserver' in window && this.getBoundingClientRect().top > innerHeight) {
        this.classList.remove('products-visible');
        this.revealObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.classList.add('products-visible');
            this.revealObserver.disconnect();
          }
        }, { threshold: 0.08 });
        this.revealObserver.observe(this);
      }
      const update = () => {
        const maximum = this.slider.scrollWidth - this.slider.clientWidth;
        this.previous.disabled = this.slider.scrollLeft < 2;
        this.next.disabled = this.slider.scrollLeft >= maximum - 2;
      };
      const slide = (direction) => {
        const card = this.slider.querySelector('.luxury-slider__product-card');
        const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0;
        const amount = card ? card.getBoundingClientRect().width + gap : this.slider.clientWidth;
        this.slider.scrollBy({ left: direction * amount, behavior: reducedMotion() ? 'auto' : 'smooth' });
      };
      this.previous.addEventListener('click', () => slide(-1), { signal });
      this.next.addEventListener('click', () => slide(1), { signal });
      this.slider.addEventListener('scroll', update, { passive: true, signal });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(this.slider);
      } else window.addEventListener('resize', update, { signal });
      update();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.revealObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.removeAttribute('data-rail-ready');
    }
  }

  customElements.define('muvazi-product-gallery', MuvaziProductGallery);
  if (!customElements.get('muvazi-product-rail')) customElements.define('muvazi-product-rail', MuvaziProductRail);
})();
